import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DropdownMenu from './DropdownMenu';
import { mainSiteUrl, categories } from '@/mocks/categories';

export default function Navbar() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 60);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const hasBg = !isHome || scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        hasBg
          ? 'bg-background-50 shadow-[0_1px_0_0_rgba(0,0,0,0.06)] backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-3 group">
            <span
              className={`text-xl md:text-2xl font-heading font-bold tracking-tight transition-colors ${
                hasBg ? 'text-foreground-900' : 'text-white'
              }`}
            >
              Mulebuy<span className={hasBg ? 'text-accent-500' : 'text-accent-300'}>Spreadsheets</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link
              to="/"
              className={`text-sm font-medium whitespace-nowrap font-label transition-colors ${
                hasBg ? 'text-foreground-700 hover:text-accent-500' : 'text-white/90 hover:text-white'
              }`}
            >
              Home
            </Link>
            <Link
              to="/shopping-guide"
              className={`text-sm font-medium whitespace-nowrap font-label transition-colors ${
                hasBg ? 'text-foreground-700 hover:text-accent-500' : 'text-white/90 hover:text-white'
              }`}
            >
              Shopping Guide
            </Link>
            <Link
              to="/what-is-mulebuy-spreadsheet"
              className={`text-sm font-medium whitespace-nowrap font-label transition-colors ${
                hasBg ? 'text-foreground-700 hover:text-accent-500' : 'text-white/90 hover:text-white'
              }`}
            >
              Guide
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium whitespace-nowrap font-label transition-colors ${
                hasBg ? 'text-foreground-700 hover:text-accent-500' : 'text-white/90 hover:text-white'
              }`}
            >
              Main Platform
            </a>
            <DropdownMenu hasBg={hasBg} />
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors whitespace-nowrap font-label ${
                hasBg
                  ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                  : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 border border-white/30'
              }`}
            >
              Buy It Now
            </a>
          </nav>

          <div className="flex items-center gap-3 md:hidden">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors whitespace-nowrap font-label ${
                hasBg
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-white/20 backdrop-blur-sm text-white border border-white/30'
              }`}
            >
              Buy Now
            </a>
            <MobileMenu hasBg={hasBg} />
          </div>
        </div>
      </div>
    </header>
  );
}

function MobileMenu({ hasBg }: { hasBg: boolean }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-2 transition-colors ${hasBg ? 'text-foreground-700' : 'text-white'}`}
      >
        <i className={`text-xl ${isOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
      </button>
      {isOpen && (
        <div className="absolute top-16 left-0 right-0 bg-background-50 border-b border-background-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col p-4 gap-1">
            <Link to="/" onClick={() => setIsOpen(false)} className="px-4 py-3 text-sm font-medium text-foreground-700 rounded-md hover:bg-background-100 font-label">
              <i className="ri-home-4-line mr-2"></i>Home
            </Link>
            <Link to="/shopping-guide" onClick={() => setIsOpen(false)} className="px-4 py-3 text-sm font-medium text-foreground-700 rounded-md hover:bg-background-100 font-label">
              <i className="ri-shopping-bag-3-line mr-2"></i>Shopping Guide
            </Link>
            <Link to="/what-is-mulebuy-spreadsheet" onClick={() => setIsOpen(false)} className="px-4 py-3 text-sm font-medium text-foreground-700 rounded-md hover:bg-background-100 font-label">
              <i className="ri-book-open-line mr-2"></i>Guide
            </Link>
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="px-4 py-3 text-sm font-medium text-foreground-700 rounded-md hover:bg-background-100 font-label">
              <i className="ri-external-link-line mr-2"></i>Main Platform
            </a>

            <div className="border-t border-background-200 my-2 pt-2">
              <p className="px-4 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider font-label">Categories</p>
              {categories.map((cat) => (
                <a
                  key={cat.seq}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2.5 text-sm text-foreground-600 rounded-md hover:bg-background-100 hover:text-accent-600 transition-colors font-label flex items-center justify-between"
                >
                  {cat.name}
                  <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}